﻿/* global BABYLON */

export const RoomAlign = {
  left: 0,
  center: 1,
  right: 2,
  bottom: 3,
  top: 4,
  back: 5,
  front: 6,
};

/**
 * This class is responsible for creating a room into which we can place the product.
 * Initial development was done here: https://playground.babylonjs.com/#BSHTJZ#33 based on the work
 * done by <unknown> here: https://playground.babylonjs.com/#CGA05F#66.
 * */
export class Room {
  constructor(scene) {
    this.scene = scene;
    this.DEFAULT_ROOM_DIMENSIONS = {
      width: 3000,
      depth: 2000,
      height: 2500
    };
    this.BASEBOARD_HEIGHT = 100;
    this.align = {};
    this.materials = {};
  }

  createColorMaterial(name, color, alpha, removeSpecularColor) {
    if (!color)
      return;

    let material = this.materials[name];
    if (!material) {
      material = new BABYLON.StandardMaterial(name, this.scene);
      this.materials[name] = material;
    }
    material.diffuseColor = color.length === 7
      ? BABYLON.Color3.FromHexString(color)
      : BABYLON.Color4.FromHexString(color);
    if (removeSpecularColor) {
      // Remove glare from the wall:
      material.specularColor = BABYLON.Color3.Black();
    }
    if (alpha !== undefined) {
      material.alpha = alpha;
    }
    return material;
  }

  removeRoomFromDrawing() {
    if (this.floor) {
      this.floor.dispose();
      this.floor = null;
    }
    if (this.walls) {
      this.walls.forEach(wall => wall.dispose());
      this.walls = null;
    }
    if (this.baseboards) {
      this.baseboards.forEach(baseboard => baseboard.dispose());
      this.baseboards = null;
    }
  }

  createFloor(options) {
    const { floorMaterial, origin, dimensions } = options;
    // In this case the 'height' of the ground is actually the depth of the room
    // because the ground is a 2 dimensional object.
    const floor = BABYLON.MeshBuilder.CreateGround(
      "Floor",
      {
        width: dimensions.width,
        height: dimensions.depth
      },
      this.scene
    );

    // Due to a phenomenon called 'z-fighting', we want the floor to be a smidge lower than all the meshes we load into the scene
    floor.position = new BABYLON.Vector3(origin.x, origin.y - 0.01, origin.z);
    if (floorMaterial) {
      floor.material = floorMaterial;
    }
    floor.isPickable = false;
    return floor;
  }

  createWalls({ wallData, origin = { x: 0, y: 0, z: 0 }, dimensions = this.DEFAULT_ROOM_DIMENSIONS,
    baseboardMaterial = null }) {
    const walls = new Array(4);
    const baseboards = this.includeBaseboards ? new Array(4) : null;
    wallData.forEach((wallDatum, index) => {
      const { rotation, material } = wallDatum;
      const isShortWall = !index || index === 3;
      const width = isShortWall ? dimensions.depth : dimensions.width;
      const wall = BABYLON.MeshBuilder.CreatePlane(
        `Wall ${index}`,
        { width, height: dimensions.height },
        this.scene
      );
      walls[index] = wall;

      const multiplier = index < 2 ? -1 : 1;
      const x = multiplier * (isShortWall ? dimensions.width / 2 : 0);
      const y = dimensions.height / 2;
      const z = multiplier * (!isShortWall ? dimensions.depth / 2 : 0);
      wall.position = new BABYLON.Vector3(
        origin.x + x,
        origin.y + y,
        origin.z + z);
      wall.rotationQuaternion = new BABYLON.Quaternion.FromEulerAngles(
        rotation.x,
        rotation.y,
        rotation.z
      );
      wall.material = material;
      wall.isPickable = false;

      if (this.includeBaseboards) {
        const baseboard = new BABYLON.MeshBuilder.CreatePlane(
          `baseboard${index}`,
          { width: width, height: this.BASEBOARD_HEIGHT, depth: 0.1 },
          this.scene
        );
        const baseboardX = x > 0 ? x - 5 : x + 5;
        const baseboardZ = z > 0 ? z - 5 : z + 5;
        baseboard.position = new BABYLON.Vector3(
          origin.x + baseboardX,
          origin.y + this.BASEBOARD_HEIGHT / 2,
          origin.z + baseboardZ
        );
        baseboards[index] = baseboard;
        baseboard.rotationQuaternion = new BABYLON.Quaternion.FromEulerAngles(
          rotation.x,
          rotation.y,
          rotation.z
        );
        baseboard.material = baseboardMaterial;
        baseboard.isPickable = false;
      }
    });
    return { walls, baseboards };
  }

  updateSizesAndPositions() {
    const dims = this.contentDims = {
    }
    if (this.content) {
      this.content.computeWorldMatrix();
      let bbox = this.content.getBoundingInfo().boundingBox;
      dims.min = bbox.minimumWorld;
      dims.max = bbox.maximumWorld;
      dims.size = dims.max.subtract(dims.min);
      const leftGap = this.leftGap || 0;
      const rightGap = this.rightGap || 0;
      if (this.autoWidth) {
        this.width = dims.size.x + leftGap + rightGap;
        const deltaGap = (leftGap - rightGap) / 2;
        this.content.position.x = deltaGap;
      } else if (this.align.x === RoomAlign.left) {
        this.content.position.x = -(this.width / 2) - dims.min.x + leftGap;
      } else if (this.align.x === RoomAlign.right) {
        this.content.position.x = (this.width / 2) - dims.max.x - rightGap;
      }

      const bottomGap = this.bottomGap || 0;
      const topGap = this.topGap || 0;
      if (this.autoHeight) {
        this.height = dims.size.y + topGap + bottomGap;
        const deltaGap = (bottomGap - topGap) / 2;
        this.content.position.y = deltaGap;
      } else if (this.align.y === RoomAlign.bottom) {
        this.content.position.y = -(this.height / 2) - dims.min.y + bottomGap;
      } else if (this.align.y === RoomAlign.top) {
        this.content.position.y = (this.height / 2) - dims.max.y - topGap;
      }

      const frontGap = this.frontGap || 0;
      const backGap = this.backGap || 0;
      if (this.autoDepth) {
        this.depth = dims.size.z + frontGap + backGap;
        const deltaGap = (frontGap - backGap) / 2;
        this.content.position.z = deltaGap;
      } else if (this.align.z === RoomAlign.front) {
        this.content.position.z = -(this.depth / 2) - dims.min.z + frontGap;
      } else if (this.align.z === RoomAlign.back) {
        this.content.position.z = (this.depth / 2) - dims.max.z - backGap;
      }
    } else {
      dims.min = BABYLON.Vector3.Zero();
      dims.max = BABYLON.Vector3.Zero();
      dims.size = BABYLON.Vector3.Zero();
    }

    this.roomDimensions = {
      width: this.width || this.DEFAULT_ROOM_DIMENSIONS.width,
      height: this.height || this.DEFAULT_ROOM_DIMENSIONS.height,
      depth: this.depth || this.DEFAULT_ROOM_DIMENSIONS.depth,
    };
  }

  rebuildAndAddToDrawing() {
    this.removeRoomFromDrawing();
    this.updateSizesAndPositions();
    // Use this.floorMaterial if assigned. Otherwise, create a color material
    const floorMaterial = this.floorMaterial ||
      this.createColorMaterial('Floor material', this.floorColor, this.floorAlpha);
    const options = {
      floorMaterial,
      origin: { x: 0, y: -this.roomDimensions.height / 2, z: 0 },
      dimensions: this.roomDimensions
    };
    this.floor = this.createFloor(options);

    const wallMaterial = this.wallMaterial ||
      this.createColorMaterial('Wall material', this.wallColor, this.wallAlpha, true);
    const baseboardMaterial = this.includeBaseboards
      ? this.baseboardMaterial || this.createColorMaterial('Baseboard material', this.baseboardColor, this.baseboardAlpha, true)
      : null;
    const wallData = [
      {
        // Left
        rotation: new BABYLON.Vector3(0, -Math.PI / 2, 0),
        material: wallMaterial
      },
      {
        // Front
        rotation: new BABYLON.Vector3(0, Math.PI, 0),
        material: wallMaterial
      },
      {
        // Back
        rotation: new BABYLON.Vector3(0, 0, 0),
        material: wallMaterial
      },
      {
        // Right
        rotation: new BABYLON.Vector3(0, Math.PI / 2, 0),
        material: wallMaterial
      }
    ];

    const { walls, baseboards } = this.createWalls({
      wallData,
      origin: { x: 0, y: -this.roomDimensions.height / 2, z: 0 },
      dimensions: this.roomDimensions,
      baseboardMaterial
    });
    this.walls = walls;
    this.baseboards = baseboards;
  }
}
